import { z } from "zod";

/**
 * Position webhook payload sent by MT5 EA and Playwright bot alike.
 *
 * Wire format:
 *   POST api.pfh.trading/webhooks/position
 *   Headers:
 *     X-PFH-Timestamp: <unix ms>
 *     X-PFH-Signature: hex(hmac_sha256(EA_WEBHOOK_SECRET, ts + "." + raw_body))
 *     Content-Type:    application/json
 *   Body: PositionEvent
 *
 * Worker middleware verifies HMAC with a 5-minute replay window, then parses
 * with `PositionEventZ.parse()`.
 */
export const PositionEventTypeZ = z.enum(["open", "close", "modify", "heartbeat"]);
export type PositionEventType = z.infer<typeof PositionEventTypeZ>;

export const PositionEventZ = z.object({
  accountId: z.string().min(1).max(32),
  // ticket > 0 — the bot's drivers parse this from broker DOM and
  // mid-render rows can read as 0; rejecting them at the schema layer
  // means a ghost frame never traverses the network or paints a phantom
  // row on the dashboard.
  ticket: z.number().int().positive(),
  symbol: z.string().max(16),
  side: z.enum(["buy", "sell"]),
  // `heartbeat` events legitimately report 0 lots when no position is
  // open; only `open`/`modify` events carry real size. nonnegative()
  // accepts both without gating every tick on having a live trade.
  lots: z.number().nonnegative(),
  entry: z.number(),
  stopLoss: z.number().nullable(),
  takeProfit: z.number().nullable(),
  equity: z.number(),
  balance: z.number(),
  marginLevel: z.number().nullable(),
  openPnl: z.number(),
  closedPnl: z.number().nullable().optional(),
  event: PositionEventTypeZ,
  timestampMs: z.number().int(),
});
export type PositionEvent = z.infer<typeof PositionEventZ>;
